import { useEffect, useMemo, useState } from "react";
import InputField from "../shared-components/InputField";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../shared-components/Button";
import DropdownCombo, {
  DropdownItem,
} from "../shared-components/DropdownCombo";
import {
  BlueprintItemSourceInfoMinimal,
  BlueprintItemTypeInfoMinimal,
  CreateBlueprintSources,
  CreateBlueprintTemplate,
  READABLE_TURN_AROUND,
  TURN_AROUND_CALCULATIONS,
} from "../api/Blueprints";
import Container from "../shared-components/Container";
import PageHeader from "../shared-components/PageHeader";
import FormTwoColumnContainer from "../shared-components/FormTwoColumnContainer";
import LabeledCheckbox from "../shared-components/LabeledCheckbox";

import { useNavigate, useSearchParams } from "react-router-dom";
import {
  itemTypeToUI,
  useCreateBlueprintTemplate,
  useGetReportTypesRoot,
  useGetWorkflowRootBlueprintsList,
  useGetWorkflowStepNames,
} from "../api/BlueprintsApi";
import { blueprint_tooltip } from "../utils";
import {
  InformationCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { checkTypeColorItems, getBehaviors } from "../utils";
import ErrorMessageContainer from "../shared-components/ErrorMessageContainer";
import { behaviorMap, useFamaFlags } from "../api/FamaFlags";

interface BluprintFormErrors {
  sla: boolean;
  lookback: boolean;
  turn_around: boolean;
  blueprint_root_uuid: boolean;
  name: boolean;
  sources: boolean;
  submissionTried: boolean;
  areInstagramTypesSelected: boolean;
}

type BlueprintSourceOption = {
  field: string;
  display: string;
};

interface SourceOptionsDict {
  [key: string]: BlueprintSourceOption[];
}
const sourceOptions: SourceOptionsDict = {
  youtube: [
    {
      field: "transcribe",
      display: "YouTube Video",
    },
  ],
};

export function buildBlueprintItemTypesList(options: {
  itemTypes: BlueprintItemTypeInfoMinimal[];
}): BlueprintItemTypeInfoMinimal[] {
  const instagramPosts = options.itemTypes.find(
    (itemType) => itemType.name === "scrape_instagram_posts"
  );

  const instagramReels = options.itemTypes.find(
    (itemType) => itemType.name === "scrape_instagram_reels"
  );

  const blueprintItemTypes: BlueprintItemTypeInfoMinimal[] = [];

  if (instagramPosts) {
    blueprintItemTypes.push({
      ...instagramPosts,
      item_type_uuid: instagramPosts.item_type_uuid,
    });
  }
  if (instagramReels) {
    blueprintItemTypes.push({
      ...instagramReels,
      item_type_uuid: instagramReels.item_type_uuid,
    });
  }
  return blueprintItemTypes;
}

function isInstagramSourceSelectionValid(
  blueprintForm: CreateBlueprintTemplate
) {
  return blueprintForm.blueprint_sources.some(
    (source) => source.name.toLowerCase() === "instagram"
  )
    ? blueprintForm.blueprint_types.length > 0 // at least one selection so is ok
    : true; // if instagram is not selected, then it is valid
}

// Base item types we show with display names (diligence is gated by v3DiligenceEnabled)
const BASE_ITEM_TYPE_DISPLAY_MAP: { [key: string]: string } = {
  news: "360 News",
  legal_cases: "Legal cases",
  summaries: "Summary",
  fama_reports: "Fama Report",
};

/**
 * BlueprintForm component for creating new blueprint templates.
 *
 * Supports query string parameters for default behavior selection:
 * - behaviors: Comma-separated list of behavior names to pre-select
 *   Example: ?behaviors=HARASSMENT,INTOLERANCE,SEX,VIOLENCE,THREAT,CRIMINAL,CANNABIS,OTHER%20DRUGS,COMMUNITY
 *
 * Available behavior names: HARASSMENT, INTOLERANCE, SEX, CANNABIS, COMMUNITY, CRIMINAL, OTHER DRUGS, THREAT, VIOLENCE
 */
const BlueprintForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Parse behaviors from query parameters
  const behaviorsParam = searchParams.get("behaviors");
  const defaultBehaviors = behaviorsParam
    ? behaviorsParam.split(",").map((b) => b.trim())
    : [];
  const {
    data: workflowBlueprintList,
    mutate: getWorklowBlueprintList,
    isLoading: isLoadingList,
  } = useGetWorkflowRootBlueprintsList();

  const {
    data: reportTypesList,
    mutate: getReportTypes,
    isLoading: isLoadingReportTypes,
  } = useGetReportTypesRoot();

  const {
    mutate: CreateBlueprintTemplate,
    isLoading: isLoadingCreateBlueprint,
    error: CreateBlueprintError,
    isSuccess: isCreateSuccess,
  } = useCreateBlueprintTemplate();
  const { behaviorMappingActions, v3DiligenceEnabled } = useFamaFlags();
  const itemTypeDisplayMap = useMemo((): Record<string, string> => {
    const map: Record<string, string> = { ...BASE_ITEM_TYPE_DISPLAY_MAP };
    if (v3DiligenceEnabled) {
      map.diligence = "Diligence";
    }
    return map;
  }, [v3DiligenceEnabled]);
  const getItemTypeDisplayName = (name: string) =>
    itemTypeDisplayMap[name] || name;
  const sortedBehaviors = getBehaviors();

  const {
    data: workflowStepNames,
    mutate: getWorkflowStepNames,
    isLoading: isLoadingStepNames,
  } = useGetWorkflowStepNames();

  const [selectedSteps, setSelectedSteps] = useState<string[]>([]);
  const [showSkipStepsSection, setShowSkipStepsSection] =
    useState<boolean>(false);

  // Initialize behaviors based on query parameters or default to all behaviors
  const initialBehaviors =
    defaultBehaviors.length > 0
      ? sortedBehaviors
          .filter((behavior) =>
            defaultBehaviors.some(
              (queryBehavior) =>
                queryBehavior.toLowerCase() === behavior[0].toLowerCase()
            )
          )
          .map((behavior) => ({ name: behavior[0] }))
      : sortedBehaviors.map((behavior) => ({ name: behavior[0] }));

  const [blueprintForm, setBlueprintForm] = useState<CreateBlueprintTemplate>({
    sla_hours: -1,
    lookback: -1,
    blueprint_root_uuid: "",
    name: "",
    description: "",
    blueprint_behavior: initialBehaviors,
    blueprint_sources: [],
    blueprint_types: [],
    color: "",
    report_type: "MISCONDUCT",
    steps_to_skip: "",
  });

  const [errors, setErrors] = useState<BluprintFormErrors>({
    sla: false,
    lookback: false,
    turn_around: false,
    blueprint_root_uuid: false,
    name: false,
    sources: false,
    submissionTried: false,
    areInstagramTypesSelected: false,
  });
  const [workflowItems, setWorkflowItems] = useState<DropdownItem[]>([]);
  const [reportTypes, setReportTypes] = useState<DropdownItem[]>([]);
  const [options, setSelectorsState] = useState<{
    itemSources: BlueprintItemSourceInfoMinimal[];
    itemTypes: BlueprintItemTypeInfoMinimal[];
    behaviors: [string, string][];
  }>({
    itemSources: [],
    itemTypes: [],
    behaviors: [],
  });
  const turnAroundOptions: DropdownItem[] = [
    { id: TURN_AROUND_CALCULATIONS.Hour, label: READABLE_TURN_AROUND.Hour },
    { id: TURN_AROUND_CALCULATIONS.Days, label: READABLE_TURN_AROUND.Days },
    {
      id: TURN_AROUND_CALCULATIONS.SingleBusinessDay,
      label: READABLE_TURN_AROUND.SingleBusinessDay,
    },
    {
      id: TURN_AROUND_CALCULATIONS.ThreeSixty,
      label: READABLE_TURN_AROUND.ThreeSixty,
    },
  ];

  useEffect(() => {
    getWorklowBlueprintList();
    getReportTypes();
    getWorkflowStepNames();
  }, [getWorklowBlueprintList, getReportTypes, getWorkflowStepNames]);

  const sortSourcesByName = (
    a: BlueprintItemSourceInfoMinimal,
    b: BlueprintItemSourceInfoMinimal
  ) => {
    // sorting by name inside object
    const nameA = a.name.toUpperCase(); // ignore upper and lowercase
    const nameB = b.name.toUpperCase(); // ignore upper and lowercase
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }

    // names must be equal
    return 0;
  };

  function fillBlueprintItemTypes(itemTypes: BlueprintItemTypeInfoMinimal[]) {
    const newTypes = itemTypes.filter((option) => {
      return (
        option.is_fixed ||
        blueprintForm.blueprint_types.some(
          (type) => option.item_type_uuid === type.item_type_uuid
        )
      );
    });
    // Convert to CreateBlueprintItemTypes format like Instagram reels
    return newTypes.map((itemType) => ({
      name: itemType.name,
      item_type_uuid: itemType.item_type_uuid,
      external_id: itemType.blueprintItemType_uuid,
      is_active: itemType.isActive ?? true,
      is_fixed: itemType.is_fixed ?? false,
    }));
  }

  const onWorkflowChange = (blueprint_root_uuid: string) => {
    const selectedWorkflow = workflowBlueprintList?.find(
      (item) => item.external_id === blueprint_root_uuid
    );
    if (selectedWorkflow) {
      // generete checkbox options
      const itemSources =
        selectedWorkflow?.sources.sort(sortSourcesByName) || [];

      // Use all item types from the workflow
      const itemTypes = selectedWorkflow?.item_types || [];

      setSelectorsState({
        itemSources,
        itemTypes,
        behaviors: sortedBehaviors,
      });

      // memory already selected
      const newSources = itemSources.filter((option) => {
        return (
          option.is_fixed ||
          blueprintForm.blueprint_sources.some(
            (source) => option.item_source_uuid === source.item_source_uuid
          )
        );
      });

      const newBehavior = blueprintForm.blueprint_behavior.filter(
        (behavior) => {
          return sortedBehaviors.some((option) => option[0] === behavior.name);
        }
      );
      setBlueprintForm({
        ...blueprintForm,
        blueprint_root_uuid: blueprint_root_uuid,
        blueprint_behavior: newBehavior,
        blueprint_sources: newSources,
        blueprint_types: [],
      });
    }
  };

  useEffect(
    () => {
      const list =
        workflowBlueprintList?.map((item) => ({
          id: item.external_id || "",
          label: item.name || "",
        })) || [];
      if (list.length > 0) {
        setWorkflowItems(list);
        onWorkflowChange((workflowBlueprintList || [])[0]?.external_id || "");
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workflowBlueprintList, setWorkflowItems]
  );

  useEffect(() => {
    const list =
      reportTypesList?.map((item) => ({
        id: item || "",
        label: item || "",
      })) || [];
    if (list.length > 0) {
      setReportTypes(list);
    }
  }, [reportTypesList, setReportTypes]);

  const onReportTypeChange = (reportType: string) => {
    setBlueprintForm({
      ...blueprintForm,
      report_type: reportType,
    });
  };

  const onBlueprintItemTypeChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!e.target.checked) {
      const newTypes = blueprintForm.blueprint_types.filter((type) => {
        return type.item_type_uuid !== e.target.id;
      });
      setBlueprintForm({ ...blueprintForm, blueprint_types: newTypes });
    } else {
      const newTypes = [...blueprintForm.blueprint_types];
      // Find the item type from the workflow data
      const itemType = options.itemTypes.find(
        (item) => item.item_type_uuid === e.target.id
      );

      if (itemType) {
        // Use the full object structure like Instagram reels
        newTypes.push({
          name: itemType.name,
          item_type_uuid: itemType.item_type_uuid,
          external_id: itemType.blueprintItemType_uuid,
          is_active: itemType.isActive ?? true,
          is_fixed: itemType.is_fixed ?? false,
        });
      }
      setBlueprintForm({ ...blueprintForm, blueprint_types: newTypes });
    }
  };

  const onSourceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // If checked is removed, remove from Set
    if (!e.target.checked) {
      const newSources = blueprintForm.blueprint_sources.filter((source) => {
        return source.name !== e.target.name;
      });
      setBlueprintForm({
        ...blueprintForm,
        blueprint_sources: newSources,
        blueprint_types: [],
      });
    } else {
      const newSources = [...blueprintForm.blueprint_sources];
      newSources.push({ name: e.target.name, item_source_uuid: e.target.id });
      const newTypes = fillBlueprintItemTypes(options.itemTypes);
      setBlueprintForm({
        ...blueprintForm,
        blueprint_sources: newSources,
        blueprint_types: newTypes,
      });
    }
  };

  const onSourceOptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sourceAndOption = e.target.name.toLocaleLowerCase();
    const checked = e.target.checked;
    const option_source = sourceAndOption.split("^")[0];
    const option_name = sourceAndOption.split("^")[1];
    const newSources = [...blueprintForm.blueprint_sources];
    const activeSource: CreateBlueprintSources | undefined = newSources.find(
      (source) => source.name.toLocaleLowerCase() === option_source
    );
    if (activeSource) {
      const existing_options = activeSource.options || {};
      existing_options[option_name] = checked;
      activeSource.options = existing_options;
      setBlueprintForm({
        ...blueprintForm,
        blueprint_sources: newSources,
      });
    }
  };

  const onBehaviorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const behaviorName = e.target.name;
    const isChecked = e.target.checked;
    let newBehaviors;

    if (isChecked) {
      // Add the behavior to the list
      newBehaviors = [
        ...blueprintForm.blueprint_behavior,
        { name: behaviorName },
      ];
    } else {
      // Remove the behavior from the list
      newBehaviors = blueprintForm.blueprint_behavior.filter(
        (behavior) => behavior.name !== behaviorName
      );
    }

    setBlueprintForm({ ...blueprintForm, blueprint_behavior: newBehaviors });
  };

  const handleStepToggle = (stepName: string) => {
    if (selectedSteps.includes(stepName)) {
      setSelectedSteps(selectedSteps.filter((step) => step !== stepName));
    } else {
      setSelectedSteps([...selectedSteps, stepName]);
    }

    setBlueprintForm({
      ...blueprintForm,
      steps_to_skip: [...selectedSteps, stepName]
        .filter(
          (step) => step !== stepName || !selectedSteps.includes(stepName)
        )
        .join(","),
    });
  };

  useEffect(() => {
    const newErrors = {
      sla:
        blueprintForm.sla_hours < 0 &&
        blueprintForm.turn_around === TURN_AROUND_CALCULATIONS.Hour,
      lookback: blueprintForm.lookback < 0,
      turn_around: !blueprintForm.turn_around,
      blueprint_root_uuid: blueprintForm.blueprint_root_uuid.length === 0,
      name: blueprintForm.name.length === 0,
      behaviors: blueprintForm.blueprint_behavior.length === 0,
      sources: blueprintForm.blueprint_sources.length === 0,
      areInstagramTypesSelected:
        !isInstagramSourceSelectionValid(blueprintForm),
    };
    setErrors((errors) => ({
      ...newErrors,
      submissionTried: errors.submissionTried,
    }));
  }, [blueprintForm, setErrors]);

  useEffect(() => {
    if (isCreateSuccess) {
      navigate(-1);
    }
  }, [isCreateSuccess, navigate]);

  function handleSave(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    e.preventDefault();
    if (
      !errors.sla &&
      !errors.lookback &&
      !errors.blueprint_root_uuid &&
      !errors.name &&
      !errors.sources
    ) {
      if (
        blueprintForm.turn_around === TURN_AROUND_CALCULATIONS.Days ||
        blueprintForm.turn_around ===
          TURN_AROUND_CALCULATIONS.SingleBusinessDay ||
        blueprintForm.turn_around === TURN_AROUND_CALCULATIONS.ThreeSixty
      ) {
        CreateBlueprintTemplate({
          ...blueprintForm,
          sla_hours: 0,
          steps_to_skip: selectedSteps.join(","),
        });
      } else {
        CreateBlueprintTemplate({
          ...blueprintForm,
          steps_to_skip: selectedSteps.join(","),
        });
      }
    }

    setErrors({ ...errors, submissionTried: true });
  }

  const showError = (): boolean => {
    const formError =
      errors.sla ||
      errors.lookback ||
      errors.blueprint_root_uuid ||
      errors.name ||
      errors.sources;
    return errors.submissionTried && (formError || !!CreateBlueprintError);
  };

  const isSourceSelected = (sourceName: string): boolean => {
    return blueprintForm.blueprint_sources.some(
      (source) =>
        source.name.toLocaleLowerCase() === sourceName.toLocaleLowerCase()
    );
  };

  const isOptionChecked = (
    sourceName: string,
    optionField: string
  ): boolean => {
    const sources = blueprintForm.blueprint_sources.filter(
      (s) => s.name.toLocaleLowerCase() === sourceName.toLocaleLowerCase()
    );
    if (sources && sources.length === 1) {
      const s = sources[0];
      if (s.options && s.options.hasOwnProperty(optionField)) {
        return s.options[optionField];
      }
    }
    return false;
  };

  const getFilteredItemTypes = (): BlueprintItemTypeInfoMinimal[] => {
    const allowedTypes = Object.keys(itemTypeDisplayMap);
    return options.itemTypes.filter((itemType) =>
      allowedTypes.includes(itemType.name)
    );
  };

  return (
    <>
      <PageHeader title="Create Check Type"></PageHeader>
      <Container>
        <form>
          <FormTwoColumnContainer title="Check Type Name">
            <InputField
              handleChange={(e) => {
                setBlueprintForm({ ...blueprintForm, name: e.target.value });
              }}
              name={"name"}
              placeholder=""
              label={"Check Type Name*"}
            />
            <div className="pt-2">
              <InputField
                handleChange={(e) => {
                  setBlueprintForm({
                    ...blueprintForm,
                    description: e.target.value,
                  });
                }}
                name={"description"}
                placeholder=""
                label={"Description"}
              />
            </div>
            <div className="pt-2">
              <DropdownCombo
                items={checkTypeColorItems}
                name={"check-type-color"}
                label={"Color Assignment*"}
                placeHolder={"Default - Black"}
                onChange={(e) =>
                  setBlueprintForm({
                    ...blueprintForm,
                    color: e.id,
                  })
                }
              ></DropdownCombo>
            </div>
          </FormTwoColumnContainer>
          <FormTwoColumnContainer title="Report Type">
            <DropdownCombo
              items={reportTypes}
              onChange={(e) => onReportTypeChange(e.id)}
              placeHolder={
                isLoadingReportTypes ? "Loading" : "Select Report Type"
              }
              label={"Report Type (Product)*"}
              name="blueprint-report-type"
            ></DropdownCombo>
          </FormTwoColumnContainer>

          <FormTwoColumnContainer title="Workflow Setting">
            <DropdownCombo
              items={workflowItems}
              onChange={(e) => onWorkflowChange(e.id)}
              placeHolder={isLoadingList ? "Loading" : "Select Company"}
              label={"Workflow Setting*"}
              name="blueprint-workflow-setting"
              initial={workflowItems?.[0]}
            ></DropdownCombo>
            <div className="pt-2 mb-2 text-sm font-medium text-gray-600">
              Select Sources*
            </div>
            <div className="mt-4 text-sm border rounded-lg">
              <div className="flex border-b p-2">
                {isLoadingList ? (
                  <LoadingSpinner></LoadingSpinner>
                ) : (
                  <div className="flex-1">
                    {options.itemSources.map(
                      (item) =>
                        !item.is_fixed && (
                          <div
                            className="pb-2"
                            key={item.blueprintItemSource_uuid}
                          >
                            <LabeledCheckbox
                              id={item.item_source_uuid}
                              label={item.name}
                              name={item.name}
                              onChange={onSourceChange}
                            ></LabeledCheckbox>
                            <div className="ml-4 ">
                              {item.name.toLocaleLowerCase() in sourceOptions &&
                                isSourceSelected(item.name) && (
                                  <div>
                                    {sourceOptions[
                                      item.name.toLocaleLowerCase()
                                    ].map(
                                      (sourceOption: BlueprintSourceOption) => (
                                        <div
                                          className="pt-2"
                                          key={`${item.name}^${sourceOption.field}`}
                                        >
                                          <LabeledCheckbox
                                            id={`${item.name}^${sourceOption.field}`}
                                            label={sourceOption.display}
                                            name={`${item.name}^${sourceOption.field}`}
                                            checked={isOptionChecked(
                                              item.name,
                                              sourceOption.field
                                            )}
                                            onChange={onSourceOptionChange}
                                          />
                                        </div>
                                      )
                                    )}
                                  </div>
                                )}
                              {item.name.toLocaleLowerCase() === "instagram" &&
                                isSourceSelected("instagram") && (
                                  <>
                                    {buildBlueprintItemTypesList({
                                      itemTypes: options.itemTypes,
                                    }).map((itemType) => (
                                      <div
                                        className="pt-2"
                                        key={itemType.item_type_uuid}
                                      >
                                        <LabeledCheckbox
                                          id={itemType.item_type_uuid}
                                          label={itemTypeToUI(itemType)}
                                          name={itemType.name}
                                          defaultChecked={true}
                                          onChange={onBlueprintItemTypeChange}
                                        ></LabeledCheckbox>
                                      </div>
                                    ))}
                                    {!isInstagramSourceSelectionValid(
                                      blueprintForm
                                    ) && (
                                      <div className="py-2">
                                        <ErrorMessageContainer message="At least one source selection is needed" />
                                      </div>
                                    )}
                                  </>
                                )}
                            </div>
                          </div>
                        )
                    )}
                  </div>
                )}
              </div>
            </div>
          </FormTwoColumnContainer>

          <FormTwoColumnContainer title="Item Types">
            <div className="pt-2 mb-2 text-sm font-medium text-gray-600">
              Select Item Types
            </div>
            <div className="mt-4 text-sm border rounded-lg">
              <div className="flex border-b p-2">
                {isLoadingList ? (
                  <LoadingSpinner></LoadingSpinner>
                ) : (
                  <div className="flex-1">
                    {getFilteredItemTypes().map((itemType) => (
                      <div className="pb-2" key={itemType.item_type_uuid}>
                        <LabeledCheckbox
                          id={itemType.item_type_uuid}
                          label={getItemTypeDisplayName(itemType.name)}
                          name={itemType.name}
                          defaultChecked={blueprintForm.blueprint_types.some(
                            (type) =>
                              type.item_type_uuid === itemType.item_type_uuid
                          )}
                          onChange={onBlueprintItemTypeChange}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </FormTwoColumnContainer>

          <FormTwoColumnContainer title="Check Type SLA Calculation">
            <DropdownCombo
              items={turnAroundOptions}
              onChange={(e) => {
                setBlueprintForm({
                  ...blueprintForm,
                  turn_around: e.id as TURN_AROUND_CALCULATIONS,
                });
              }}
              label={"Report Turnaround Calculation"}
              html={true}
              name="blueprint-turnaround-calculation"
              tooltip={blueprint_tooltip}
              icon={
                <InformationCircleIcon height={24} width={24} color="grey" />
              }
            ></DropdownCombo>
            {blueprintForm.turn_around === TURN_AROUND_CALCULATIONS.Hour && (
              <div className="mt-4 mb-6">
                <InputField
                  handleChange={(e) => {
                    setBlueprintForm({
                      ...blueprintForm,
                      sla_hours: parseInt(e.target.value),
                    });
                  }}
                  type={"number"}
                  name={"sla"}
                  placeholder=""
                  label={"Blueprint SLA (in Hours)"}
                  labelClassName="mb-0 w-80"
                  classes="flex items-center"
                  min={0}
                  className="ml-4 w-16"
                />
              </div>
            )}
          </FormTwoColumnContainer>
          <FormTwoColumnContainer title="Lookback">
            <div className="mb-6">
              <InputField
                handleChange={(e) => {
                  setBlueprintForm({
                    ...blueprintForm,
                    lookback: parseInt(e.target.value),
                  });
                }}
                type={"number"}
                name={"look-back-period"}
                placeholder=""
                label={"Set Lookback period for Check Type (month)"}
                labelClassName="mb-0 w-80"
                classes="flex items-center"
                min={0}
                className="ml-4 w-16"
              />
            </div>
          </FormTwoColumnContainer>

          <FormTwoColumnContainer
            title="Fama behavior flag"
            className="mt-12 w-8/12"
          >
            {isLoadingList ? (
              <LoadingSpinner></LoadingSpinner>
            ) : (
              <div className="flex gap-4 p-4 flex-col border-2 rounded-lg w-96">
                {options.behaviors.map((item) => (
                  <div className="capitalize font-normal my-1" key={item[0]}>
                    <LabeledCheckbox
                      defaultChecked={blueprintForm.blueprint_behavior.some(
                        (behavior) => behavior.name === item[0]
                      )}
                      toolTip={item[1]}
                      label={behaviorMap(
                        item[0],
                        behaviorMappingActions,
                        "key"
                      )}
                      name={item[0]}
                      onChange={onBehaviorChange}
                    ></LabeledCheckbox>
                  </div>
                ))}
              </div>
            )}
          </FormTwoColumnContainer>

          <FormTwoColumnContainer
            title="Workflow Steps to Skip"
            className="mt-12"
          >
            <div className="flex items-center justify-start mb-4">
              <button
                onClick={() => setShowSkipStepsSection(!showSkipStepsSection)}
                className="text-gray-400 focus:outline-none"
                type="button"
              >
                <div className="flex items-center justify-start  ">
                  <div className="text-sm font-medium text-gray-600">
                    Select workflow steps to skip during execution
                  </div>
                  {showSkipStepsSection ? (
                    <ChevronUpIcon className="w-6 h-6" />
                  ) : (
                    <ChevronDownIcon className="w-6 h-6" />
                  )}
                </div>
              </button>
            </div>

            {showSkipStepsSection && (
              <div className="mt-4 text-sm border rounded-lg">
                <div className="flex border-b p-2">
                  {isLoadingStepNames ? (
                    <LoadingSpinner />
                  ) : (
                    <div className="flex-1">
                      {workflowStepNames?.map((stepName) => (
                        <div className="pb-2" key={stepName}>
                          <LabeledCheckbox
                            id={stepName}
                            label={stepName}
                            name={stepName}
                            onChange={() => handleStepToggle(stepName)}
                            checked={selectedSteps.includes(stepName)}
                            preserveLabelCasing={true}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </FormTwoColumnContainer>

          {showError() && (
            <div className="p-4 my-4 bg-red-300 text-red-900 rounded-lg">
              <ul>
                {errors.name && <li>Check Type name cannot be empty.</li>}
                {errors.blueprint_root_uuid && (
                  <li>Workflow Setting is required.</li>
                )}
                {errors.sources && (
                  <li>At least one source should be selected.</li>
                )}
                {errors.sla && <li>SLA is a required field.</li>}
                {errors.turn_around && (
                  <li>Turn around is a required field.</li>
                )}
                {errors.lookback && <li>Lookback is a required field.</li>}
                {errors.areInstagramTypesSelected && (
                  <li>
                    If instagram is selected, posts or reels must be selected
                  </li>
                )}
                {!isCreateSuccess && !!CreateBlueprintError && (
                  <li>Server error processing</li>
                )}
              </ul>
            </div>
          )}
          <div className="border-t pt-6">
            <FormTwoColumnContainer className="mb-0">
              <Button
                text="Cancel"
                className="mr-2"
                colorType={ButtonColorTypes.cancel}
                onClick={() => {
                  navigate("/check-types");
                }}
              />
              <Button
                type={isLoadingCreateBlueprint ? ButtonTypes.saving : undefined}
                disabled={isLoadingList || isLoadingCreateBlueprint}
                text="Save Check Type"
                className="w-56"
                onClick={(e) => handleSave(e)}
              />
            </FormTwoColumnContainer>
          </div>
        </form>
      </Container>
    </>
  );
};

export default BlueprintForm;
